/**
 * ContactAnalytics.js
 * -------------------------------
 * Analytics tracking for contact interactions.
 * Supports Google Analytics (gtag) and custom client-side analytics.
 *
 * Features:
 * - Contact button click tracking
 * - Modal interaction tracking
 * - Custom event data collection
 * - Fallback logging when analytics unavailable
 */

class ContactAnalytics {
    /**
     * Track contact button click
     * @param {HTMLElement} button The clicked button
     * @param {Object} context Additional context data
     * @returns {boolean} True if tracking was successful
     */
    static trackContactClick(button, context = {}) {
        try {
            const eventData = this.buildClickEventData(button, context);
            
            // Try Google Analytics first
            if (this.trackWithGoogleAnalytics('contact_button_click', eventData)) {
                return true;
            }
            
            // Fallback to client analytics
            return this.trackWithClientAnalytics('contact_button_click', eventData);
            
        } catch (error) {
            console.error('📊 Analytics tracking failed:', error);
            return false;
        }
    }

    /**
     * Track modal interaction
     * @param {string} action The modal action (opened, closed, submitted, etc.)
     * @param {Object} context Additional context data
     * @returns {boolean} True if tracking was successful
     */
    static trackModalInteraction(action, context = {}) {
        try {
            const eventData = {
                event: 'contact_modal_interaction',
                modal_action: action,
                timestamp: new Date().toISOString(),
                page_url: typeof window !== 'undefined' ? window.location.href : 'unknown',
                ...context
            };

            // Try Google Analytics first
            if (this.trackWithGoogleAnalytics('contact_modal_interaction', eventData)) {
                return true;
            }
            
            // Fallback to client analytics
            return this.trackWithClientAnalytics('contact_modal_interaction', eventData);
            
        } catch (error) {
            console.error('📊 Modal analytics tracking failed:', error);
            return false;
        }
    }

    /**
     * Build event data for button clicks
     * @param {HTMLElement} button The clicked button
     * @param {Object} context Additional context
     * @returns {Object} Event data object
     */
    static buildClickEventData(button, context = {}) {
        try {
            return {
                event: 'contact_button_click',
                button_text: button?.textContent?.trim() || 'unknown',
                button_id: button?.id || 'no-id',
                button_class: button?.className || 'no-class',
                button_location: this.getButtonLocation(button),
                page_url: typeof window !== 'undefined' ? window.location.href : 'unknown',
                page_title: typeof document !== 'undefined' ? document.title : 'unknown',
                timestamp: new Date().toISOString(),
                user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
                ...context
            };
        } catch (error) {
            console.error('📊 Failed to build click event data:', error);
            return {
                event: 'contact_button_click',
                error: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }

    /**
     * Determine button location on page
     * @param {HTMLElement} button The button element
     * @returns {string} Button location description
     */
    static getButtonLocation(button) {
        try {
            if (!button) return 'unknown';

            // Check if button is in header
            if (button.closest('header, .header, nav, .nav')) {
                return 'header';
            }

            // Check if button is in footer
            if (button.closest('footer, .footer')) {
                return 'footer';
            }

            // Check if button is in sidebar
            if (button.closest('aside, .sidebar, .side-nav')) {
                return 'sidebar';
            }

            // Check if button is in main content
            if (button.closest('main, .main, .content')) {
                return 'main-content';
            }

            // Default location
            return 'page-body';

        } catch (error) {
            return 'unknown';
        }
    }

    /**
     * Track with Google Analytics (gtag)
     * @param {string} eventName Event name
     * @param {Object} eventData Event data
     * @returns {boolean} True if successful
     */
    static trackWithGoogleAnalytics(eventName, eventData) {
        try {
            if (typeof window === 'undefined') {
                return false;
            }

            if (typeof window.gtag === 'function') {
                window.gtag('event', eventName, eventData);
                console.log('📊 Google Analytics tracked:', { eventName, eventData });
                return true;
            } else {
                console.log('gtag not available');
                return false;
            }
        } catch (error) {
            console.error('📊 Google Analytics tracking failed:', error);
            return false;
        }
    }

    /**
     * Track with client-side analytics (fallback)
     * @param {string} eventName Event name
     * @param {Object} eventData Event data
     * @returns {boolean} True if successful
     */
    static trackWithClientAnalytics(eventName, eventData) {
        try {
            // Log to console for debugging
            console.log('📊 Client analytics:', eventData);

            // Store in localStorage for offline collection (optional)
            if (typeof window !== 'undefined' && window.localStorage) {
                try {
                    const analyticsKey = 'contact_analytics_events';
                    const existingEvents = JSON.parse(localStorage.getItem(analyticsKey) || '[]');
                    existingEvents.push({ eventName, eventData, timestamp: Date.now() });
                    
                    // Keep only the last 100 events
                    if (existingEvents.length > 100) {
                        existingEvents.splice(0, existingEvents.length - 100);
                    }
                    
                    localStorage.setItem(analyticsKey, JSON.stringify(existingEvents));
                } catch (storageError) {
                    // Ignore storage errors
                }
            }

            // In a real application, you might send this to your analytics endpoint
            // this.sendToAnalyticsEndpoint(eventName, eventData);

            return true;
        } catch (error) {
            console.error('📊 Client analytics tracking failed:', error);
            return false;
        }
    }

    /**
     * Send event to custom analytics endpoint (placeholder)
     * @param {string} eventName Event name
     * @param {Object} eventData Event data
     */
    static async sendToAnalyticsEndpoint(eventName, eventData) {
        try {
            // This is where you would send data to your backend analytics service
            // Example:
            /*
            const response = await fetch('/api/analytics', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ event: eventName, data: eventData })
            });
            
            if (!response.ok) {
                throw new Error(`Analytics endpoint responded with ${response.status}`);
            }
            */
            
            console.log('📊 Would send to analytics endpoint:', { eventName, eventData });
        } catch (error) {
            console.error('📊 Failed to send to analytics endpoint:', error);
        }
    }

    /**
     * Get stored analytics events from localStorage
     * @returns {Array} Array of stored analytics events
     */
    static getStoredEvents() {
        try {
            if (typeof window === 'undefined' || !window.localStorage) {
                return [];
            }

            const analyticsKey = 'contact_analytics_events';
            const storedEvents = localStorage.getItem(analyticsKey);
            return storedEvents ? JSON.parse(storedEvents) : [];
        } catch (error) {
            console.error('📊 Failed to get stored analytics events:', error);
            return [];
        }
    }

    /**
     * Clear stored analytics events
     */
    static clearStoredEvents() {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.removeItem('contact_analytics_events');
                console.log('📊 Cleared stored analytics events');
            }
        } catch (error) {
            console.error('📊 Failed to clear stored analytics events:', error);
        }
    }

    /**
     * Get analytics summary
     * @returns {Object} Analytics summary
     */
    static getAnalyticsSummary() {
        try {
            const events = this.getStoredEvents();
            const summary = {
                totalEvents: events.length,
                eventTypes: {},
                lastEvent: events.length > 0 ? events[events.length - 1] : null,
                oldestEvent: events.length > 0 ? events[0] : null
            };

            // Count event types
            events.forEach(event => {
                const eventName = event.eventName || 'unknown';
                summary.eventTypes[eventName] = (summary.eventTypes[eventName] || 0) + 1;
            });

            return summary;
        } catch (error) {
            console.error('📊 Failed to get analytics summary:', error);
            return {
                totalEvents: 0,
                eventTypes: {},
                lastEvent: null,
                oldestEvent: null,
                error: error.message
            };
        }
    }
}

export default ContactAnalytics;